// Fixed-tilt placement module.
//
// Step-by-step build per agent-os/specs/solar-flat-roof-master-ssot.md
// All Fixed-tilt logic lives here. `panel.js` only knows to call us via the
// dispatcher — it should not import anything Fixed-tilt-specific.

import * as THREE from "three";
import { InstancedMesh2 } from "@three.ez/instanced-mesh";
import { DEFAULT_SUN_ALTITUDE_DEC21_NOON, PANEL_THICKNESS_M } from "../constants";
import { paintPanelEdgesBlack } from "./panel";
import { getCurrentAnchor, setCurrentAnchor } from "./common";


// ---------------------------------------------------------------------------
// World-space north vector — used to derive default azimuth.
// GLTF drone deliverables use ENU convention: model-local +Y = geographic
// north. Applying `project.orientation` Euler to (0, 1, 0) gives world-north.
// ---------------------------------------------------------------------------

export const computeWorldNorthVector = function (project) {
  const orientation = project?.orientation ?? { x: -90, y: 0, z: 0 };
  const euler = new THREE.Euler(
    THREE.MathUtils.degToRad(orientation.x || 0),
    THREE.MathUtils.degToRad(orientation.y || 0),
    THREE.MathUtils.degToRad(orientation.z || 0),
    "XYZ"
  );
  return new THREE.Vector3(0, 1, 0).applyEuler(euler).normalize();
};

// ---------------------------------------------------------------------------
// Hemisphere from latitude — sign of latitude is the only thing that matters.
// Equator (0°) defaults to N; the math is symmetric there.
// ---------------------------------------------------------------------------

export const hemisphereFromLatitude = function (latitudeDeg) {
  if (latitudeDeg == null || Number.isNaN(latitudeDeg)) return "N";
  return latitudeDeg >= 0 ? "N" : "S";
};

// ---------------------------------------------------------------------------
// Compass basis on the roof plane.
//
// Returns { north, east } unit vectors lying in the roof plane (perpendicular
// to `normalOut`), aligned with geographic north/east. This is the basis used
// to interpret `azimuth`: 0° = facing north, 90° = east (clockwise), 180° =
// south, 270° = west.
//
// Returns null if the roof normal is parallel to world-north (e.g. a wall
// that runs east-west tilted straight up) — caller falls back to using
// `verticalVector` as the basis.
// ---------------------------------------------------------------------------

export const computeCompassBasis = function (normalOut, project) {
  const worldNorth = computeWorldNorthVector(project);
  const northOnPlane = worldNorth
    .clone()
    .addScaledVector(normalOut, -worldNorth.dot(normalOut));
  if (northOnPlane.lengthSq() < 1e-6) return null;
  northOnPlane.normalize();
  // east = north × outwardNormal  (right-handed, looking down from above)
  const eastOnPlane = new THREE.Vector3().crossVectors(northOnPlane, normalOut).normalize();
  return { north: northOnPlane, east: eastOnPlane };
};


// ---------------------------------------------------------------------------
// Default azimuth for a Fixed-tilt / East-West array.
//
// Azimuth is measured clockwise from geographic north (0° = north, 180° =
// south). Default points panels at the equator: 180° for northern hemisphere,
// 0° for southern. Hemisphere is derived from `project.latitude` — the
// `hemisphere` arg is kept as an override for tests.
// ---------------------------------------------------------------------------

export const computeDefaultAzimuth = function (area, project, hemisphere) {
  const hemi = hemisphere ?? hemisphereFromLatitude(project?.latitude);
  return hemi === "N" ? 180 : 0;
};

// ---------------------------------------------------------------------------
// Inter-row spacing formula — gap (m) = H · sin(θ) / tan(sun_altitude)
//
// Caller derives `sunAltDeg` from project latitude via `computeSunAltitude`.
// Returns gap in METERS. Caller converts to cm for UI display.
// ---------------------------------------------------------------------------

export const computeInterRowSpacing = function (panelHeightM, tiltDeg, sunAltDeg) {
  const theta = (tiltDeg * Math.PI) / 180;
  const alt   = (sunAltDeg * Math.PI) / 180;
  // Safety floor: at high latitudes near polar circles, winter sun altitude
  // can hit 0 or go negative. Without this floor we'd divide by ~0.
  if (Math.tan(alt) < 1e-3) return panelHeightM * 3;
  return (panelHeightM * Math.sin(theta)) / Math.tan(alt);
};

// ---------------------------------------------------------------------------
// Tilted instanced mesh — DoubleSide so panels are visible from underneath
// when the camera looks up from inside an array.
// ---------------------------------------------------------------------------

const createTiltedPanelInstance = function (panelWidth, panelHeight, panelTexture, panelsCount) {
  const geometry = new THREE.BoxGeometry(panelWidth, panelHeight, PANEL_THICKNESS_M);
  paintPanelEdgesBlack(geometry);
  const material = panelTexture
    ? new THREE.MeshBasicMaterial({ map: panelTexture, vertexColors: true, transparent: true, opacity: 0.5, side: THREE.DoubleSide })
    : new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5, side: THREE.DoubleSide });
  return new InstancedMesh2(this.renderer, panelsCount, geometry, material);
};

// ---------------------------------------------------------------------------
// Main placement function — Fixed-tilt geometry.
//
// Reuses area infrastructure from `panel.js` via `this.*` (normal calc,
// inner plane, projected corners, screen data, containment check) but writes
// its own grid layout in the tilted-panel coordinate frame.
// ---------------------------------------------------------------------------

export const populateFixedTiltArea = async function (
  area,
  persist = true,
  forceFlipNormal = false,
  areaDragged = false
) {
  // -------------------------------------------------------------------------
  // Phase 1: roof plane setup
  //
  // Normal-direction convention (important — caused a real bug):
  //
  //   `area.normal` in this codebase points INTO the roof (down for a flat
  //   roof). Flush exploits this by computing
  //       offset = normal × (offsetCm / 100)
  //       pos    = gp.sub(offset)        ← SUBTRACT, not add
  //   to lift panels above the surface.
  //
  //   Tilted-mode geometry formulas (`faceDir`, `panelUp`, etc.) are written
  //   assuming the standard math convention: normal points OUT of the surface.
  //   We bridge the two by reading `area.normal` as `intoRoof` and working
  //   with `normalOut = -intoRoof` everywhere downstream. We pass `intoRoof`
  //   to the area-infrastructure helpers (which expect the codebase
  //   convention) and write `intoRoof` back at the end.
  // -------------------------------------------------------------------------
  const vectorPoints = area.points.map((p) => p.position);
  let intoRoof;
  if (area.normal != undefined) {
    intoRoof = new THREE.Vector3(area.normal.x, area.normal.y, area.normal.z);
  } else {
    // First-time placement (no flush before us): mirror flush's first-run
    // logic, including the recursive flip retry.
    intoRoof = this.calculatePlaneNormal(vectorPoints);
    if (forceFlipNormal || !this.isAreaInFrontOfModel(areaDragged)) {
      intoRoof.negate();
    }
    if (this.normalNeedsFlipping?.(vectorPoints[0], intoRoof) && !forceFlipNormal) {
      // Re-enter through the dispatcher so rackMode routing is respected.
      return this.populateArea(area, false, true);
    }
  }
  const normalOut = intoRoof.clone().negate();

  const center = this.getCenterPointFromVectors(vectorPoints);
  const simulatedCamera = this.getSimulatedCamera(center, intoRoof);
  const points = this.getInnerPlanePoints(area, intoRoof, simulatedCamera);

  // Rebuild stencil plane.
  if (area.innerPlane) {
    this.scene.remove(area.innerPlane);
    area.innerPlane.geometry?.dispose();
    area.innerPlane.material?.dispose();
    area.innerPlane = null;
  }
  const innerPlane = this.createInnerPlane(points, area.stencilCount);
  area.innerPlane = innerPlane;
  this.scene.add(innerPlane);

  const innerPlaneEdges = points.map((p, i) => [p, points[(i + 1) % points.length]]);
  innerPlane.edges = innerPlaneEdges;

  // -------------------------------------------------------------------------
  // Phase 2: panel + roof-plane axes (horizontalVector, verticalVector)
  // -------------------------------------------------------------------------
  const chosenPanel = this.getPanelById(area.panelId, area.orientation);
  if (!chosenPanel) return;
  const panelWidth = chosenPanel.size.width;
  const panelHeight = chosenPanel.size.height;
  const panelTexture = chosenPanel.texture;

  const rightMostPoint = this.getRightMostPoint(points, simulatedCamera);
  const leftMostPoint = this.getLeftMostPoint(points, simulatedCamera);
  const topMostPoint = this.getTopMostPoint(points, simulatedCamera);
  const bottomMostPoint = this.getBottomMostPoint(points, simulatedCamera);

  const xDir = this.getAxisDirectionInPlane(simulatedCamera, new THREE.Vector3(1, 0, 0), intoRoof);
  const yDir = this.getAxisDirectionInPlane(simulatedCamera, new THREE.Vector3(0, 1, 0), intoRoof);

  const projTL = this.calculateProjectedPoint(topMostPoint,    leftMostPoint,  xDir, yDir, false, true);
  const projTR = this.calculateProjectedPoint(topMostPoint,    rightMostPoint, xDir, yDir, true,  true);
  const projBL = this.calculateProjectedPoint(bottomMostPoint, leftMostPoint,  xDir, yDir, false, false);

  const horizontalVector = projTR.clone().sub(projTL).normalize();
  const verticalVector   = projTL.clone().sub(projBL).normalize();

  // -------------------------------------------------------------------------
  // Phase 3: tilted-mode coordinate frame
  //
  //   aziDir   — direction panels face, in the roof plane. Measured clockwise
  //              from geographic north on the roof plane:
  //                0°   = north,  90°  = east,
  //                180° = south,  270° = west.
  //              Falls back to the polygon-vertical basis when the roof normal
  //              is parallel to world north (a degenerate edge case).
  //   perpDir  — left-right panel axis, in the roof plane.
  //   faceDir  — panel face normal (tilted θ from `normal` toward `aziDir`).
  //   panelUp  — local "up the slope" direction, used as `up` in `lookAt`.
  // -------------------------------------------------------------------------
  const ft      = area.mountingConfigurations?.fixed_tilt ?? {};
  const tiltDeg = ft.tilt ?? 15;
  const tiltRad = (tiltDeg * Math.PI) / 180;
  const cosT = Math.cos(tiltRad);
  const sinT = Math.sin(tiltRad);

  const azRad   = ((ft.azimuth ?? 0) * Math.PI) / 180;
  const compass = computeCompassBasis(normalOut, this.project);
  const azBase  = compass ?? { north: verticalVector, east: horizontalVector };
  const aziDir  = azBase.north.clone()
    .multiplyScalar(Math.cos(azRad))
    .addScaledVector(azBase.east, Math.sin(azRad))
    .normalize();
  const perpDir = new THREE.Vector3().crossVectors(normalOut, aziDir).normalize();

  // faceDir = panel face direction in world (rotated `normalOut` by θ toward aziDir).
  const faceDir = new THREE.Vector3()
    .addScaledVector(normalOut, cosT)
    .addScaledVector(aziDir,    sinT);
  // panelUp = "up the slope" direction (polar/rear edge is high, equator/front edge is low).
  // Standard rack convention: rear legs taller, panel leans into the sun.
  const panelUp = new THREE.Vector3()
    .addScaledVector(aziDir,   -cosT)
    .addScaledVector(normalOut, sinT);

  // -------------------------------------------------------------------------
  // Phase 4: grid layout
  //
  //   footH       — horizontal footprint of one panel = H · cos(θ)
  //   rowPitch    — row-to-row distance (footprint + inter-row gap)
  //   colPitch    — column-to-column distance (panel width; panels touch)
  //   panelElev   — height of panel CENTER above the roof, along `normal`.
  //                 Bottom edge sits at `offset`; center is half a sin(θ) up.
  //
  // null inter_row_spacing means formula mode — compute from tilt + sun altitude.
  // A non-null value is a user override; use it directly.
  // -------------------------------------------------------------------------
  const footH = panelHeight * cosT;
  let interRowGapCm = ft.inter_row_spacing;
  if (interRowGapCm == null) {
    const gapM = computeInterRowSpacing(panelHeight, tiltDeg, DEFAULT_SUN_ALTITUDE_DEC21_NOON);
    interRowGapCm = parseFloat((gapM * 100).toFixed(1));
  }
  const interRowGap = interRowGapCm / 100;
  const rowPitch = footH + interRowGap;
  const colPitch = panelWidth;
  const panelElev = (area.offset ?? 0) / 100 + (panelHeight * sinT) / 2;

  // Grid extents — iterate symmetrically around the area CENTER so we cover
  // the polygon regardless of which direction `aziDir` points. (Anchoring at
  // a corner like projTL only works when aziDir happens to point into the
  // area from that corner — e.g. ~180° default. At azimuth 0° the iteration
  // would extend out the wrong side and only a few panels near the corner
  // would land inside the polygon.)
  const areaWidth  = leftMostPoint.distanceTo(rightMostPoint);
  const areaHeight = bottomMostPoint.distanceTo(topMostPoint);
  const maxDim = Math.sqrt(areaWidth * areaWidth + areaHeight * areaHeight);
  const iRange = Math.ceil(maxDim / (2 * rowPitch)) + 2;
  const jRange = Math.ceil(maxDim / (2 * colPitch)) + 2;

  // -------------------------------------------------------------------------
  // Phase 5: containment + restricted-area exclusion + N×N grid-offset sweep
  //
  // For each candidate cell, build the panel's roof-plane footprint corners
  // and check (a) polygon containment, (b) no overlap with any restricted
  // area. A panel is placed only if both pass. Mirrors flush's pattern in
  // `calculatePanelPlacement` (panel.js ~line 1263).
  //
  // The grid is periodic with period (rowPitch × colPitch). Any starting
  // offset outside that period is equivalent to one inside, so 8×8=64 uniform
  // samples within ONE pitch cell exhaustively cover all distinct alignments.
  // We pick the alignment that fits the most panels — without this sweep, an
  // unlucky polygon-vs-pitch ratio can lose a row entirely (e.g. one row
  // centered when shifting half a pitch would fit two).
  // -------------------------------------------------------------------------
  const areaScreenData = this.buildAreaScreenData(points, innerPlaneEdges, simulatedCamera);

  // Resolve restricted-area vertex arrays once — re-used across every
  // candidate panel in every sweep iteration.
  const restrictedAreas = (area.restrictedAreas ?? []).map((ra) => ({
    points: ra.points.map((p) =>
      new THREE.Vector3(p.position.x, p.position.y, p.position.z)
    ),
    edges: ra.edges,
  }));

  const hW = perpDir.clone().multiplyScalar(panelWidth / 2);
  const hH = aziDir.clone().multiplyScalar(footH / 2);

  // Returns true iff a panel centred at `gp` passes containment + restricted-area checks.
  const panelFits = (gp) => {
    const c0 = gp.clone().add(hW).add(hH);
    const c1 = gp.clone().sub(hW).add(hH);
    const c2 = gp.clone().sub(hW).sub(hH);
    const c3 = gp.clone().add(hW).sub(hH);
    const corners = [c0, c1, c2, c3];
    const edges   = [[c0, c1], [c1, c2], [c2, c3], [c3, c0]];
    if (!this.isRectangleWithinPoints(corners, edges, points, innerPlaneEdges, simulatedCamera, areaScreenData)) return false;
    for (const ra of restrictedAreas) {
      if (this.isRectangleIntersectingArea(corners, edges, ra.points, ra.edges, simulatedCamera)) return false;
    }
    return true;
  };

  // Lay out a deterministic grid from a single starting position.
  // The iteration order (i then j) is identical between save and reload,
  // so the resulting `grid` array is byte-stable for a given startPos.
  const layoutFromStart = (startPos) => {
    const out = [];
    for (let i = -iRange; i <= iRange; i++) {
      for (let j = -jRange; j <= jRange; j++) {
        const gp = startPos.clone()
          .addScaledVector(perpDir,  j * colPitch)
          .addScaledVector(aziDir,  -i * rowPitch);
        if (panelFits(gp)) out.push(gp);
      }
    }
    return out;
  };

  let bestGrid = [];
  let bestStart = null;

  // Sweep on first entry into this mode (no anchor yet); otherwise replay from
  // the saved anchor so a mode-switch preserves where panels were placed/dragged.
  const existingAnchor = getCurrentAnchor(area);
  if (!existingAnchor) {
    const N_SAMPLES = 8;
    for (let si = 0; si < N_SAMPLES; si++) {
      for (let sj = 0; sj < N_SAMPLES; sj++) {
        const dRow = (rowPitch * si) / N_SAMPLES;
        const dCol = (colPitch * sj) / N_SAMPLES;
        const sweepStart = center.clone()
          .addScaledVector(aziDir,  -dRow)
          .addScaledVector(perpDir,  dCol);
        const candidateGrid = layoutFromStart(sweepStart);
        if (candidateGrid.length > bestGrid.length) {
          bestGrid  = candidateGrid;
          bestStart = sweepStart;
        }
      }
    }
    // Center fallback: sweep can miss single-panel areas.
    if (bestGrid.length === 0 && panelFits(center)) {
      bestGrid  = [center.clone()];
      bestStart = center.clone();
    }
    if (bestStart) setCurrentAnchor(area, bestStart.clone());
  } else {
    const savedStart = new THREE.Vector3(existingAnchor.x, existingAnchor.y, existingAnchor.z);
    bestGrid = layoutFromStart(savedStart);
    if (bestGrid.length === 0 && panelFits(savedStart)) bestGrid = [savedStart];
  }

  const grid  = bestGrid;
  const count = bestGrid.length;

  // -------------------------------------------------------------------------
  // Phase 6: build instanced mesh
  // -------------------------------------------------------------------------
  if (area.instancedMesh) {
    this.scene.remove(area.instancedMesh);
    area.instancedMesh.dispose?.();
    area.instancedMesh = null;
  }

  let instancedMesh;
  if (count > 0) {
    instancedMesh = createTiltedPanelInstance.call(
      this, panelWidth, panelHeight, panelTexture, count
    );
    this.scene.add(instancedMesh);

    const tempMatrix     = new THREE.Matrix4();
    const tempPosition   = new THREE.Vector3();
    const tempTarget     = new THREE.Vector3();
    const tempQuaternion = new THREE.Quaternion();
    const tempScale      = new THREE.Vector3(1, 1, 1);

    for (let idx = 0; idx < grid.length; idx++) {
      const gp = grid[idx];

      // Position: lift the gp on the roof plane up (along normalOut = OUT of roof)
      // by panelElev.
      tempPosition.set(gp.x, gp.y, gp.z).addScaledVector(normalOut, panelElev);

      // Three.js Matrix4.lookAt: panel face = (eye - target).normalize().
      // To make the panel face point along `faceDir`, target must be at
      // `position - faceDir`. (Mirrors flush, where target = position + normal
      // with normal = INTO roof, giving face = OUT of roof.)
      tempTarget.copy(tempPosition).sub(faceDir);

      tempQuaternion.setFromRotationMatrix(
        new THREE.Matrix4().lookAt(tempPosition, tempTarget, panelUp)
      );
      tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
      instancedMesh.setMatrixAt(idx, tempMatrix);
    }
    instancedMesh.instanceMatrix.needsUpdate = true;

    // Bulk-move drag (`onDragSolarGroup` in area.js) reads and mutates
    // `instancedMesh.seed`. If unset, seed.add(translationVector) throws and
    // the `instanceMatrix.needsUpdate = true` line right after never runs —
    // so panels appear frozen mid-drag even though their matrices are
    // changing. Pick the first panel's position as the seed (flush picks a
    // specific index based on placement strategy; we don't have that
    // structure here, but any in-array position is sufficient since seed is
    // only used as a translation reference, not a geometric anchor).
    instancedMesh.getMatrixAt(0, tempMatrix);
    tempMatrix.decompose(tempPosition, tempQuaternion, tempScale);
    instancedMesh.seed = tempPosition.clone();
  }

  // -------------------------------------------------------------------------
  // Phase 7: store on area (mirrors flush so move/rotate/check work)
  //
  // `testPanel` is needed by `checkPanelsInSolarArea` (panel.js) which is the
  // post-placement validator that re-runs whenever a restricted area is
  // moved/resized. Without it that pipeline silently no-ops on tilted areas.
  // -------------------------------------------------------------------------
  const infinitePlane = new THREE.Plane().setFromNormalAndCoplanarPoint(intoRoof, center);

  area.instancedMesh    = instancedMesh;
  area.normal           = intoRoof; // Keep INTO-roof convention so flush still works on switch back.
  area.ground           = verticalVector;
  area.simulatedCamera  = simulatedCamera;
  area.verticalVector   = verticalVector;
  area.horizontalVector = horizontalVector;
  area.aziDir           = aziDir;
  area.perpDir          = perpDir;
  area.infinitePlane    = infinitePlane;
  area.areaCenter       = center;
  area.testPanel        = this.createTempPanel(chosenPanel);

  // Run the same post-placement validator flush uses (see panel.js ~line 871).
  // This is the hook live restricted-area drag/resize relies on: that flow
  // mutates `area.restrictedAreas` and then calls `checkPanelsInSolarArea`,
  // which iterates `area.instancedMesh` and toggles per-instance visibility.
  if (instancedMesh) {
    this.checkPanelsInSolarArea(area);
    const panelsCenter = this.calculatePanelsCenter(area);
    area.panelsCenter = panelsCenter;
    this.refreshSolarGroupLabels(area, panelsCenter, horizontalVector);
  }

  if (persist) this.createUpdateSolarGroup(area);
};
